import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import type { AuthUiConfig } from '@orborix/auth-client';
import { AppShell } from './components/shell/AppShell.js';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { RequireAuth, RedirectIfAuthed } from './auth/RequireAuth.js';
import { AppLoader } from './components/loading/AppLoader.js';
import { ConnectionError } from './components/loading/ConnectionError.js';
import { OtpLoginScreen } from './screens/auth/OtpLoginScreen.js';
import { RedirectIfOnboarded } from './screens/onboarding/RedirectIfOnboarded.js';
import { AiConsentGate } from './screens/onboarding/AiConsentGate.js';
import { A0Gated } from './screens/leveling/A0Gated.js';
import { HomeOrA0 } from './screens/home/HomeOrA0.js';
import { PracticeDeckSection } from './screens/library/practiceDeck.js';
import { ConversationScreen } from './screens/conversation/ConversationScreen.js';
import { recordPwaSession } from './pwa/index.js';
import { useNativeShell } from './lib/useNativeShell.js';
import { PushNotificationsBridge } from './lib/pushNotifications.js';
import { PushPrePrompt } from './components/push/PushPrePrompt.js';
import { markOtaAppReady } from './lib/native/ota.js';
import { OtaBridge } from './lib/native/OtaBridge.js';
import { SentryUserBridge } from './lib/SentryUserBridge.js';
import { AnalyticsBridge } from './lib/AnalyticsBridge.js';

// Code-splitting por pantalla: el bundle único rozaba el límite HARD de 2 MiB
// del precache de Workbox (vite-plugin-pwa aborta el build al cruzarlo).
// Eager quedan sólo el shell + el camino crítico de voz — login, HomeOrA0
// (incluye el motor A0) y ConversationScreen: abrir la charla no puede esperar
// el fetch de un chunk. El resto carga on-demand; los chunks lazy entran
// igual al precache del SW, así que offline sigue completo. Las navegaciones
// posteriores no muestran el fallback (react-router envuelve en
// startTransition y sostiene la pantalla anterior durante el load).
const WelcomeOrigin = lazy(() =>
  import('./screens/onboarding/WelcomeOrigin.js').then((m) => ({ default: m.WelcomeOrigin })),
);
const GoalScreen = lazy(() =>
  import('./screens/onboarding/GoalScreen.js').then((m) => ({ default: m.GoalScreen })),
);
const RoutineOnboardingScreen = lazy(() =>
  import('./screens/routine/RoutineScreens.js').then((m) => ({
    default: m.RoutineOnboardingScreen,
  })),
);
const RoutineEditScreen = lazy(() =>
  import('./screens/routine/RoutineScreens.js').then((m) => ({ default: m.RoutineEditScreen })),
);
const OnboardingLevel = lazy(() =>
  import('./screens/leveling/OnboardingLevel.js').then((m) => ({ default: m.OnboardingLevel })),
);
const LevelResult = lazy(() =>
  import('./screens/leveling/LevelResult.js').then((m) => ({ default: m.LevelResult })),
);
const ProgressScreen = lazy(() =>
  import('./screens/progress/ProgressScreen.js').then((m) => ({ default: m.ProgressScreen })),
);
const DebriefScreen = lazy(() =>
  import('./screens/debrief/DebriefScreen.js').then((m) => ({ default: m.DebriefScreen })),
);
const LibraryScreen = lazy(() =>
  import('./screens/library/LibraryScreen.js').then((m) => ({ default: m.LibraryScreen })),
);
const ActivityScreen = lazy(() =>
  import('./screens/activity/ActivityScreen.js').then((m) => ({ default: m.ActivityScreen })),
);
const SessionDetailScreen = lazy(() =>
  import('./screens/activity/SessionDetailScreen.js').then((m) => ({
    default: m.SessionDetailScreen,
  })),
);
const ScenarioHub = lazy(() =>
  import('./screens/scenarios/ScenarioHub.js').then((m) => ({ default: m.ScenarioHub })),
);
const ScenarioCreate = lazy(() =>
  import('./screens/scenarios/ScenarioCreate.js').then((m) => ({ default: m.ScenarioCreate })),
);
const ScenarioBriefing = lazy(() =>
  import('./screens/scenarios/ScenarioBriefing.js').then((m) => ({ default: m.ScenarioBriefing })),
);
// `/program` is the can-do path (plan 46) or today's Program, by the
// `canDoPath` flag as the API answers it (see ProgramRoute).
const ProgramRoute = lazy(() =>
  import('./screens/program/ProgramRoute.js').then((m) => ({ default: m.ProgramRoute })),
);
const GrammarLibraryRoute = lazy(() =>
  import('./screens/program/can-do/CanDoLibraryScreen.js').then((m) => ({
    default: m.GrammarLibraryRoute,
  })),
);
const ListenScreen = lazy(() =>
  import('./screens/listen/ListenScreen.js').then((m) => ({ default: m.ListenScreen })),
);
const ListenPlayerScreen = lazy(() =>
  import('./screens/listen/ListenPlayerScreen.js').then((m) => ({
    default: m.ListenPlayerScreen,
  })),
);
const ProfileScreen = lazy(() =>
  import('./screens/profile/ProfileScreen.js').then((m) => ({ default: m.ProfileScreen })),
);
const PersonalizeScreen = lazy(() =>
  import('./screens/profile/PersonalizeScreen.js').then((m) => ({ default: m.PersonalizeScreen })),
);
const MemoryScreen = lazy(() =>
  import('./screens/profile/MemoryScreen.js').then((m) => ({ default: m.MemoryScreen })),
);
const HelpScreen = lazy(() =>
  import('./screens/help/HelpScreen.js').then((m) => ({ default: m.HelpScreen })),
);

// Dev panel (CEFR computation, live session signals, what ran): dev server and
// cabrix previews only (`__DEV_TOOLS__`, vite.config.ts), so the constant-folded
// `null` keeps its chunk out of the prod build.
const DevPanel = __DEV_TOOLS__ ? lazy(() => import('./dev/DevPanel.js')) : null;

/** Mounts the dev panel only with a session: its endpoint needs auth. */
function DevPanelGate() {
  const { user } = useAuth();
  if (!DevPanel || !user) return null;
  return (
    <Suspense fallback={null}>
      <DevPanel />
    </Suspense>
  );
}

// Root '/' (and unknown paths) resolve to home or login depending on session.
function RootRedirect() {
  const { user, loading } = useAuth();
  // Visible loading is handled by RequireAuth/SplashOrb on guarded routes.
  if (loading) return null;
  return <Navigate to={user ? '/home' : '/login'} replace />;
}

// `/biblioteca/:vista` → `/library/:view`. La sección se renombró a inglés;
// las PWAs instaladas y los bookmarks siguen apuntando a la URL vieja, así que
// el mapeo de las vistas viaja con el redirect (una vista desconocida cae al
// índice, que ya resuelve a "explore").
const LEGACY_LIBRARY_VIEWS: Readonly<Record<string, string>> = {
  explorar: 'explore',
  'lo-mio': 'mine',
  practicar: 'practice',
};

function LegacyLibraryRedirect() {
  const { vista } = useParams();
  const view = vista ? LEGACY_LIBRARY_VIEWS[vista] : undefined;
  return <Navigate to={view ? `/library/${view}` : '/library'} replace />;
}

// UI de marca que los guards compartidos (@orborix/auth-client) inyectan:
// splash de sesión (AppLoader) y pantalla de error de boot con reintento.
const AUTH_UI: AuthUiConfig = {
  splash: (
    <div className="app auth-shell">
      <AppLoader page />
    </div>
  ),
  connectionError: (retry) => <ConnectionError onRetry={retry} />,
  loginPath: '/login',
  homePath: '/home',
};

export function App() {
  // OTA: this bundle booted — the React root just committed its first render.
  // Deliberately before the auth gate: the session hydrate is a network call, and
  // confirming after it would roll back users who are offline or on a slow line
  // (appReadyTimeout, lib/native/ota.ts). A render that throws never commits, so a
  // broken bundle never confirms. No-op on the web.
  useEffect(() => {
    markOtaAppReady();
  }, []);

  // Ciclo de vida nativo (Android back button + deep links). No-op en web.
  useNativeShell();

  // Cuenta la sesión para gatear el banner de instalación PWA (ver pwa/).
  useEffect(() => {
    recordPwaSession();
  }, []);

  return (
    <AuthProvider ui={AUTH_UI}>
      {/* Push nativo: listeners + re-registro del token. Necesita useAuth →
          va DENTRO del provider (como componente, no hook de App). */}
      <PushNotificationsBridge />
      {/* "¿Te avisamos?": el pedido del permiso, una vez, en Home tras la 1ra charla. */}
      <PushPrePrompt />
      {/* OTA early access: the session's own flag → the updater's custom_id. */}
      <OtaBridge />
      {/* Sentry: the account's UUID (never the email) on every event. */}
      <SentryUserBridge />
      {/* PostHog: identify by UUID, reset on sign-out / deletion, CEFR person property. */}
      <AnalyticsBridge />
      <DevPanelGate />
      <AppShell>
        {/* Fallback SÓLO del primer load directo de una pantalla lazy (deep
            link / reload): las navegaciones in-app sostienen la vista previa. */}
        <Suspense
          fallback={
            <div className="app auth-shell">
              <AppLoader page />
            </div>
          }
        >
          {/* AI data consent (store review): a signed-in account that has not
              accepted it sees the consent screen in place of any route. The
              routes below are not re-indented to keep this diff small. */}
          <AiConsentGate>
          <Routes>
            {/* Entrada única passwordless (OTP por email): registra Y loguea.
              Redirect to /home if already signed in. */}
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <OtpLoginScreen />
                </RedirectIfAuthed>
              }
            />
            {/* Rutas legacy del flujo con password (links viejos / bookmarks /
              emails ya enviados) → todas resuelven en el flujo OTP único. */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/login/code" element={<Navigate to="/login" replace />} />
            <Route path="/forgot" element={<Navigate to="/login" replace />} />
            <Route path="/reset" element={<Navigate to="/login" replace />} />
            <Route path="/verify-email" element={<Navigate to="/login" replace />} />
            {/* El motor A0 ya no tiene ruta propia: vive bajo /home (ver HomeOrA0).
              Redirect legacy por si quedó un bookmark/deep-link a la vieja /a0. */}
            <Route path="/a0" element={<Navigate to="/home" replace />} />

            {/* Guarded routes — require an authenticated session. */}
            <Route
              path="/welcome"
              element={
                <RequireAuth>
                  <RedirectIfOnboarded>
                    <WelcomeOrigin />
                  </RedirectIfOnboarded>
                </RequireAuth>
              }
            />
            <Route
              path="/onboarding/goal"
              element={
                <RequireAuth>
                  <RedirectIfOnboarded>
                    <GoalScreen />
                  </RedirectIfOnboarded>
                </RequireAuth>
              }
            />
            {/* «Tu rutina» (card #547): el compromiso, que se ofrece al terminar la
              prueba de nivel (salvo A0, ver pathAfterLevel). No va con
              RedirectIfOnboarded: quien llega ya terminó el nivel. Se cambia
              después desde el Perfil (/profile/routine). */}
            <Route
              path="/onboarding/routine"
              element={
                <RequireAuth>
                  <RoutineOnboardingScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/onboarding/level"
              element={
                <RequireAuth>
                  <OnboardingLevel />
                </RequireAuth>
              }
            />
            <Route
              path="/onboarding/level/result"
              element={
                <RequireAuth>
                  <LevelResult />
                </RequireAuth>
              }
            />
            {/* /home despacha por nivel (ver HomeOrA0): A0 → motor de
              reconocimiento, resto → Home normal. El motor A0 NO tiene ruta
              propia — la URL es siempre /home. */}
            <Route
              path="/home"
              element={
                <RequireAuth>
                  <HomeOrA0 />
                </RequireAuth>
              }
            />
            {/* Progreso (plan 27): tres tabs por intención, URL = estado.
              `/progress` default → Camino; `/progress/:tab` deep-linkea Radar. */}
            <Route
              path="/progress"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ProgressScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            {/* Tabs renombrados a inglés — los deep-links viejos siguen vivos. */}
            <Route path="/progress/escalon" element={<Navigate to="/progress/step" replace />} />
            <Route path="/progress/totales" element={<Navigate to="/progress/totals" replace />} />
            <Route
              path="/progress/:tab"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ProgressScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/conversation"
              element={
                <RequireAuth>
                  {/* Plan 33: el A0 entra SÓLO a una charla guiada a0 (lleva
                    `a0Mission` en el state); la conversación pro sigue locked. */}
                  <A0Gated allowA0Chat>
                    <ConversationScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/debrief/:sessionId"
              element={
                <RequireAuth>
                  <DebriefScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/scenarios"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ScenarioHub />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/scenarios/create"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ScenarioCreate />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/scenarios/briefing"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ScenarioBriefing />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/program"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ProgramRoute />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/program/grammar"
              element={
                <RequireAuth>
                  <A0Gated>
                    <GrammarLibraryRoute />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/listen"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ListenScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/listen/player"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ListenPlayerScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            {/* Órbitas (plan 44) está APAGADA (card #525, 2026-10-13): sin
              entrada en el Home y el link viejo cae al Inicio. La pantalla
              sigue en screens/orbitas/; prenderla = volver a montar
              `OrbitasScreen` acá (lazy, RequireAuth + A0Gated, A1+ como
              /listen) y `OrbitaEntryCard` arriba de los héroes del Home. */}
            <Route path="/orbitas" element={<Navigate to="/home" replace />} />
            {/* Biblioteca 20.H: tres vistas por intención, URL = estado (H1). */}
            <Route path="/biblioteca" element={<Navigate to="/library" replace />} />
            <Route path="/biblioteca/:vista" element={<LegacyLibraryRedirect />} />
            {/* La pantalla Vocabulario se absorbió en Lo mío (card #348); el link
              viejo sigue vivo en PWAs instaladas y cae ahí. */}
            <Route path="/vocabulary" element={<Navigate to="/library/mine" replace />} />
            {/* Layout SIN path: sostiene el deck de práctica mientras alguna
              ruta de la sección matchee, y lo suelta al salir. El deck lo arma
              el usuario desde la ficha y es material de esta visita — pero
              vivía en el estado de `LibraryScreen`, así que salir a otra
              pantalla de la sección y volver lo vaciaba. Ver `practiceDeck.tsx`. */}
            <Route element={<PracticeDeckSection />}>
              <Route
                path="/library"
                element={
                  <RequireAuth>
                    <A0Gated>
                      <LibraryScreen />
                    </A0Gated>
                  </RequireAuth>
                }
              />
              <Route
                path="/library/:view"
                element={
                  <RequireAuth>
                    <A0Gated>
                      <LibraryScreen />
                    </A0Gated>
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              path="/activity"
              element={
                <RequireAuth>
                  <A0Gated>
                    <ActivityScreen />
                  </A0Gated>
                </RequireAuth>
              }
            />
            <Route
              path="/activity/:sessionId"
              element={
                <RequireAuth>
                  <SessionDetailScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfileScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/profile/personalize"
              element={
                <RequireAuth>
                  <PersonalizeScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/profile/routine"
              element={
                <RequireAuth>
                  <RoutineEditScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/profile/memory"
              element={
                <RequireAuth>
                  <MemoryScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/help"
              element={
                <RequireAuth>
                  <HelpScreen />
                </RequireAuth>
              }
            />

            {/* Root + fallback resolve via session. */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          </AiConsentGate>
        </Suspense>
      </AppShell>
    </AuthProvider>
  );
}
